import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Shield, Lock, Eye, Terminal, Server, Globe2, AlertTriangle, Fingerprint } from 'lucide-react';

const Cybersecurity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Defense Cyber Institute";
    
    return (
        <div className="min-h-screen bg-[#0b0c10] font-sans text-[#c5c6c7] selection:bg-[#45a29e] selection:text-black font-mono">
            {/* Top Bar - System Status */}
            <div className="bg-[#1f2833] border-b border-[#45a29e]/20 py-2 px-6 flex justify-between items-center text-[10px] uppercase tracking-widest text-[#66fcf1]">
                <div className="flex gap-4">
                    <span className="flex items-center gap-2"><div className="w-2 h-2 bg-[#66fcf1] rounded-full animate-pulse"></div> Network Secure</span>
                    <span>DefCon: 4</span>
                </div>
                <div className="flex gap-6">
                    <a href="#" className="hover:text-white transition-colors">Port: 8080</a>
                    <a href="#" className="hover:text-white transition-colors">Encrypted Login</a>
                </div>
            </div>

            {/* Navbar */}
            <nav className="sticky top-0 z-50 bg-[#0b0c10]/95 backdrop-blur border-b border-[#45a29e]/30">
                <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
                    <div className="flex items-center gap-3 text-[#66fcf1]">
                        <Shield size={24} />
                        <span className="font-bold text-lg tracking-tighter">{schoolName}</span>
                    </div>

                    <div className="hidden md:flex gap-12 text-xs font-bold uppercase tracking-widest text-[#c5c6c7]">
                        <a href="#" className="hover:text-[#66fcf1] transition-colors hover:underline decoration-[#66fcf1] underline-offset-4">Blue Team</a>
                        <a href="#" className="hover:text-[#66fcf1] transition-colors hover:underline decoration-[#66fcf1] underline-offset-4">Red Team</a>
                        <a href="#" className="hover:text-[#66fcf1] transition-colors hover:underline decoration-[#66fcf1] underline-offset-4">Intel</a>
                        <a href="#" className="hover:text-[#66fcf1] transition-colors hover:underline decoration-[#66fcf1] underline-offset-4">Forensics</a>
                    </div>
                    
                    <Button variant="outline" className="border-[#66fcf1] text-[#66fcf1] hover:bg-[#66fcf1] hover:text-[#0b0c10] font-bold uppercase tracking-widest text-xs h-9 px-6 rounded-none transition-all">
                        Initialize
                    </Button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative py-32 px-6 overflow-hidden">
                {/* Grid Overlay */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(69,162,158,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(69,162,158,0.1)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>
                
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center relative z-10">
                    <div>
                         <div className="inline-flex items-center gap-2 border border-[#45a29e] px-4 py-2 rounded-sm text-[#45a29e] mb-8 bg-[#45a29e]/10">
                             <Terminal size={14} />
                             <span className="text-[10px] font-bold uppercase tracking-widest">Protocol: Classified</span>
                         </div>
                         
                         <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-none tracking-tighter">
                             Secure The <br/>
                             <span className="text-[#66fcf1]">Infrastructure.</span>
                         </h1>
                         
                         <p className="text-lg text-[#8892b0] leading-relaxed max-w-lg mb-12 border-l-2 border-[#45a29e] pl-6 font-sans">
                             Training elite operators for the digital battlefield. Master penetration testing, cryptography, and incidence response.
                         </p>
                         
                         <div className="flex gap-6">
                            <Button className="bg-[#45a29e] hover:bg-[#66fcf1] text-[#0b0c10] font-bold uppercase tracking-widest text-xs h-12 px-8 rounded-none">
                                Begin Training
                            </Button>
                            <Button variant="ghost" className="text-[#c5c6c7] hover:bg-[#1f2833] hover:text-[#66fcf1] font-bold uppercase tracking-widest text-xs h-12 px-8 rounded-none border border-[#c5c6c7]/20">
                                View Threat Map
                            </Button>
                        </div>
                    </div>

                    {/* Terminal Graphic */}
                    <div className="bg-[#000000] border border-[#1f2833] rounded-md shadow-2xl p-2 font-mono text-xs overflow-hidden h-[400px] flex flex-col">
                        <div className="flex items-center gap-2 px-2 pb-2 border-b border-[#1f2833] mb-2">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <span className="text-[#8892b0] ml-2">root@defense-node:~</span>
                        </div>
                        <div className="p-4 text-[#66fcf1] flex-1 overflow-auto space-y-2">
                            <p>&gt; init_sequence --target=all</p>
                            <p className="text-[#45a29e]">[INFO] Loading modules...</p>
                            <p className="text-[#45a29e]">[OK] NetSec loaded.</p>
                            <p className="text-[#45a29e]">[OK] CryptoLib loaded.</p>
                            <p>&gt; scan_vulnerabilities</p>
                            <p className="text-yellow-500">[WARN] Unsecured port detected: 22</p>
                            <p className="text-yellow-500">[WARN] Patching firewall rules...</p>
                            <p className="text-[#45a29e]">[OK] Firewall updated.</p>
                            <p>&gt; deploy_countermeasures</p>
                            <p className="text-green-500">... Deployed successfully.</p>
                            <p className="animate-pulse">_</p>
                        </div>
                    </div>
                </div>
            </header>

            {/* Metrics Band */}
            <div className="border-y border-[#45a29e]/30 bg-[#1f2833]/50 py-12">
                <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
                     {[
                         { icon: Globe2, label: "Global Nodes", val: "842" },
                         { icon: Lock, label: "Encrypted Bits", val: "10PB" },
                         { icon: Fingerprint, label: "Auth Requests", val: "1.2M/s" },
                         { icon: AlertTriangle, label: "Threats Blocked", val: "99.9%" },
                     ].map((stat, i) => (
                         <div key={i} className="flex items-center gap-4 border-r border-[#45a29e]/20 last:border-0">
                             <stat.icon size={32} className="text-[#45a29e]" />
                             <div>
                                 <div className="text-2xl font-bold text-white mb-1">{stat.val}</div>
                                 <div className="text-[10px] font-bold uppercase tracking-widest text-[#8892b0]">{stat.label}</div>
                             </div>
                         </div>
                     ))}
                </div>
            </div>

             {/* Footer */}
            <footer className="bg-[#0b0c10] text-[#8892b0] py-20 px-6">
                 <div className="max-w-7xl mx-auto flex flex-col items-center">
                     <div className="mb-8 p-4 border border-[#45a29e] rounded-full">
                         <Shield size={32} className="text-[#66fcf1]" />
                     </div>
                     <p className="font-mono text-xs uppercase tracking-widest max-w-lg text-center mb-12">
                         Authorized Personnel Only. <br/>
                         Unlawful access to this system is a federal offense.
                     </p>
                     
                     <div className="flex gap-16 font-mono text-[10px] font-bold uppercase tracking-widest text-[#45a29e]">
                         <a href="#" className="hover:text-white hover:underline decoration-[#66fcf1] underline-offset-4">Privacy</a>
                         <a href="#" className="hover:text-white hover:underline decoration-[#66fcf1] underline-offset-4">Terms</a>
                         <a href="#" className="hover:text-white hover:underline decoration-[#66fcf1] underline-offset-4">Encryption Keys</a>
                         <a href="#" className="hover:text-white hover:underline decoration-[#66fcf1] underline-offset-4">Contact</a>
                     </div>
                     
                     <div className="mt-12 text-[10px] opacity-40">
                         System Uptime: 99.999%
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default Cybersecurity;
